/**
 * Shared print template for Data Center charts and word clouds.
 * Same layout, @page rules, footers, and styles for BarChart and CustomerData (word cloud).
 */

const PRINT_PAGE_TITLE = "Table Mate Chart Export";

const PRINT_STYLES = `
  /* ---------- Page Setup ---------- */
  @page {
    margin: 22mm 20mm 28mm 20mm;
    size: A4;

    @top-center {
      content: "${PRINT_PAGE_TITLE}";
      font-size: 14px;
      font-weight: 600;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }

    @bottom-left {
      content: "FOOTER_LEFT_PLACEHOLDER";
      font-size: 9px;
      color: #555;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    @bottom-center {
      content: "Page " counter(page);
      font-size: 11px;
      font-weight: 600;
      color: #333;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
    @bottom-right {
      content: "FOOTER_RIGHT_PLACEHOLDER";
      font-size: 9px;
      color: #555;
      font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    }
  }

  body {
    margin: 0;
    padding: 0 0 24px 0;
    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    color: #1a1a1a;
  }

  .chart-period {
    display: none;
  }

  .chart-print-wrapper {
    max-width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 8px;
  }

  /* Keep logical sections together – avoid text/graphics cut between pages */
  .chart-print-wrapper .chart-header-row {
    page-break-after: avoid;
  }
  .chart-print-wrapper .apexcharts-canvas,
  .chart-print-wrapper .apexcharts-inner,
  .chart-print-wrapper .recharts-wrapper,
  .chart-print-wrapper table {
    page-break-inside: avoid;
    page-break-after: avoid;
  }
  .chart-print-wrapper .chart-filters-summary {
    page-break-inside: avoid;
    page-break-before: auto;
  }
  .chart-print-wrapper .chart-executive-summary-wrapper,
  .chart-print-wrapper .chart-executive-summary {
    page-break-inside: avoid;
  }

  /* Make sure the chart takes its own block with space below; no overlap */
  .chart-print-wrapper .apexcharts-canvas {
    display: block;
    margin-bottom: 8px;
  }
  .chart-print-wrapper .apexcharts-inner {
    overflow: visible !important;
  }

  .apexcharts-toolbar {
    display: none !important;
  }

  .chart-meta-actions {
    display: none;
  }

  /* Breathing room for legend – no overlap with chart */
  .apexcharts-inner {
    padding-bottom: 32px !important;
  }

  .apexcharts-legend {
    position: static !important;
    margin-top: 16px !important;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    page-break-inside: avoid;
  }

  .apexcharts-legend-series {
    margin: 0 8px !important;
  }

  .chart-filters-summary {
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    font-size: 13px;
    line-height: 1.45;
    margin-top: 8px;
  }

  .chart-filters-summary h5 {
    margin: 0 0 8px;
    font-size: 14px;
    font-weight: 600;
  }

  .chart-filters-summary ul {
    margin: 0;
    padding-left: 20px;
  }

  .chart-filters-summary li + li {
    margin-top: 6px;
  }

  .chart-executive-summary {
    margin-top: 24px;
    padding: 14px 16px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #f8fafc;
    font-size: 12px;
    line-height: 1.5;
    page-break-inside: avoid;
  }
  .chart-executive-summary-title {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
  }
  .chart-executive-summary-p {
    margin: 0 0 8px;
    font-size: 12px;
  }
  .chart-executive-summary-p:last-child {
    margin-bottom: 0;
  }

  .chart-summary-mode-toggle {
    display: none;
  }
  .chart-prompt-debug {
    display: none;
  }

  /* Tables (ChartTable): keep together and readable */
  .chart-print-wrapper table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 8px;
  }
  .chart-print-wrapper th,
  .chart-print-wrapper td {
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
  }

  /* Word cloud (CustomerData) */
  .chart-print-wrapper .wordcloud-container img {
    max-width: 100%;
    height: auto;
  }
`;

/**
 * Opens a print window with the shared Table Mate chart/export template.
 * @param {Object} options
 * @param {string} [options.title='Chart'] - Document title and print window title
 * @param {string} options.contentHtml - Inner HTML of the main content (chart or word cloud)
 * @param {string} options.summaryHtml - Full HTML for "Applied Filters & Parameters" block (e.g. <div class="chart-filters-summary">...</div>)
 * @param {Object} [options.user] - Auth user (for footer: email, restaurant name)
 * @param {string|number} [options.selectedRestaurantId] - Selected restaurant ID (for footer)
 */
export function openPrintWindow({
  title = "Chart",
  contentHtml,
  summaryHtml = "",
  user,
  selectedRestaurantId,
}) {
  const printContents = `
    <div class="chart-print-wrapper">
      ${contentHtml}
      ${summaryHtml}
    </div>
  `;

  const selectedRestaurant = user?.restaurants?.find(
    (r) => String(r.id) === String(selectedRestaurantId)
  );
  const generatedOn =
    new Date().toLocaleDateString() + " " + new Date().toLocaleTimeString();
  const footerLeft = (
    "Generated on: " +
    generatedOn +
    "  |  " +
    (user?.email || "") +
    "  |  " +
    (selectedRestaurant
      ? selectedRestaurant.restaurant + " [" + selectedRestaurantId + "]"
      : "")
  )
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');
  const footerRight = ("© Table Mate " + new Date().getFullYear())
    .replace(/\\/g, "\\\\")
    .replace(/"/g, '\\"');

  const styleWithFooters = PRINT_STYLES.replace(
    "FOOTER_LEFT_PLACEHOLDER",
    footerLeft
  ).replace("FOOTER_RIGHT_PLACEHOLDER", footerRight);

  const win = window.open("", "", "height=900,width=1200");
  win.document.write(`
    <html>
      <head>
        <title>${title}</title>
        <style>${styleWithFooters}</style>
      </head>
      <body>
        ${printContents}
      </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
  win.close();
}

/**
 * Build the standard "Applied Filters & Parameters" summary block as HTML string.
 * @param {Array<string>} items - List of <li> HTML strings (e.g. '<li><strong>X:</strong> value</li>')
 * @returns {string} Full summary div HTML
 */
export function buildFiltersSummaryHtml(items) {
  const listContent = Array.isArray(items) ? items.join("") : "";
  return `
    <div class="chart-filters-summary">
      <h5>Applied Filters &amp; Parameters</h5>
      <ul>${listContent}</ul>
    </div>
  `;
}
